import { omit } from "lodash";
import { useRouter } from "next/router";
import { useEffect } from "react";
import useUtmParams from "./useUtmParams";

const BrandId = parseInt(process.env.NEXT_PUBLIC_GENIE_TRACKING_BRAND_ID!);
let isTrackerScriptLoaded = false;
const pendingEvents: { event: string; extraData?: Record<string, any> }[] = [];

// Helper function to queue tracker operations
type TrackerOperations =
  | { type: "init"; brandId: number; email?: string }
  | { type: "setMetadata"; data: Record<string, any> }
  | { type: "track"; event: string; data?: Record<string, any> };

const queueTrackerOperation = (operation: TrackerOperations) => {
  if (isTrackerScriptLoaded && window.Tracker) {
    switch (operation.type) {
      case "init":
        window.Tracker.init(operation.brandId, operation.email);
        break;
      case "setMetadata":
        window.Tracker.setMetadata?.(operation.data);
        break;
      case "track":
        window.Tracker?.track(operation.event, operation.data);
        break;
    }
  } else {
    pendingEvents.push({
      event: operation.type,
      extraData: operation,
    });
  }
};

export const DataTrackerSetup = () => {
  const router = useRouter();
  const utmParams = useUtmParams();
  const { gt_session_id, ...restUtmParams } = utmParams || {};

  useEffect(() => {
    if (restUtmParams && router.isReady) {
      queueTrackerOperation({
        type: "setMetadata",
        data: restUtmParams,
      });
    }
  }, [restUtmParams, router.isReady]);

  useEffect(() => {
    if (!BrandId) return;

    const script = document.createElement("script");
    script.src = "https://genietracking.com/script/tracker.min.js";
    script.async = true;
    script.defer = true;
    script.id = "internal-tracker-script";
    // Don't rely on script.onload as it's not being triggered properly
    // Instead, check for Tracker in a separate effect or use a different approach
    script.onload = () => {
      console.log("Tracker script loaded");
      isTrackerScriptLoaded = true;

      // Initialize tracker with brand ID and email
      if (window.Tracker) {
        window.Tracker.init(BrandId);

        setTimeout(() => {
          document.dispatchEvent(new Event("data_tracker_initialized"));
        }, 1000);

        // Process any pending events
        if (pendingEvents.length > 0) {
          console.log("Processing pending events:", pendingEvents);
          pendingEvents.forEach(({ event, extraData }) => {
            if (!extraData) return;

            switch (event) {
              case "init":
                if ("brandId" in extraData) {
                  window.Tracker?.init(extraData.brandId, extraData.email);
                }
                break;
              case "setMetadata":
                if ("data" in extraData) {
                  window.Tracker?.setMetadata?.(extraData.data);
                }
                break;
              case "track":
                if ("event" in extraData) {
                  window.Tracker?.track(extraData.event, extraData.data);
                }
                break;
              default:
                // For backward compatibility with the old format
                window.Tracker?.track(event, extraData);
                break;
            }
          });
          pendingEvents.length = 0; // Clear the array after processing
        }
      } else {
        console.warn("Tracker not available after script load");
      }
    };
    if (!document.getElementById("internal-tracker-script")) {
      document.body.appendChild(script);
      return () => {
        document.body.removeChild(script);
        isTrackerScriptLoaded = false;
      };
    }
  }, []);
  return null;
};

export const dataTrack = (event: string, extraData?: Record<string, any>) => {
  if (typeof window === "undefined") return;
  queueTrackerOperation({ type: "track", event, data: extraData });
};
